import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { updateMetaTags } from '../utils/seo';
import { SKILL_BUNDLES, TIME, LOCATION, buildTitle, buildDescription, canonicalFor } from './idea-landing-config';

const BusinessIdeasIndex: React.FC = () => {
  // SEO
  useEffect(() => {
    const title = 'Business Ideas by Skills, Time & Work Setup | Explanatum';
    const description = 'Browse 100+ curated business idea combinations by skill bundles, time commitment, and work environment. Start the Idea Generator prefilled for your profile.';
    const url = 'https://explanatum.co.uk/business-ideas';
    updateMetaTags({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogUrl: url,
      twitterUrl: url,
      canonicalUrl: url,
      ogImage: 'https://explanatum.co.uk/og-image.jpg'
    });
    // Analytics: page view
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'view_business_ideas_index', { page_location: url });
      }
    } catch {}
  }, []);

  const links = useMemo(() => {
    const items: Array<{ to: string; label: string }> = [];
    Object.entries(SKILL_BUNDLES).forEach(([skillSlug, s]) => {
      Object.entries(TIME).forEach(([timeSlug, t]) => {
        Object.entries(LOCATION).forEach(([locSlug, l]) => {
          items.push({
            to: `/business-ideas/${skillSlug}/${timeSlug}/${locSlug}`,
            label: `${s.title} • ${t.label} • ${l.label}`
          });
        });
      });
    });
    // Prioritize first 120 combinations, adjust if needed
    return items.slice(0, 120);
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Business Ideas Index',
    description: 'A directory of programmatic landing pages for business ideas by skills, time, and work setup.',
    url: 'https://explanatum.co.uk/business-ideas'
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <article className="max-w-6xl mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Business Ideas Directory</h1>
          <p className="mt-3 text-lg text-gray-700">
            Explore curated combinations of skills, time commitment, and work setup. Each page lets you start the Idea Generator prefilled for that profile.
          </p>
        </header>

        <section aria-labelledby="browse" className="my-8">
          <h2 id="browse" className="text-xl font-semibold mb-4">Browse combinations</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {links.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => {
                    try {
                      // @ts-ignore
                      if (window && typeof window.gtag === 'function') {
                        const parts = item.to.split('/');
                        const skills_slug = parts[2+1]; // /business-ideas/{skills}/{time}/{loc}
                        const time_slug = parts[3+1];
                        const location_slug = parts[4+1];
                        // @ts-ignore
                        window.gtag('event', 'click_business_ideas_index_link', {
                          skills_slug,
                          time_slug,
                          location_slug
                        });
                      }
                    } catch {}
                  }}
                  className="block rounded-lg bg-white border p-3 hover:bg-gray-50"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Hidden semantic helper content for SEO */}
        <section aria-hidden="true" className="sr-only">
          <h2>Semantic topics</h2>
          <p>business ideas, skills, time, remote work, local business, startup ideas, side hustles, programmatic SEO</p>
        </section>
      </article>

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </main>
  );
};

export default BusinessIdeasIndex;
